// Double-Spinner Survival arena — one large round platform floating above
// water with a central rotating pillar carrying two spinner bars (lower +
// upper) that sweep in opposite directions at gradually-increasing speed.
//
// This is a SURVIVAL level, not a race: there's no finish line. Players
// spawn on the platform, dodge the bars (jump the lower one, duck/move
// for the upper), and try to stay on. Falling off = eliminated.
//
// The floor is a grid of small square tiles. Every TILE_DROP_INTERVAL_SEC
// one random tile starts shaking; 2s later it falls away leaving a gap
// the player can drop through.

import * as THREE from "three";
import { Font } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import helvetikerBold from "three/examples/fonts/helvetiker_bold.typeface.json";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";
import { makeCheeringFan } from "./World";
import { isLowQuality } from "./quality";

const ARENA_LOW = isLowQuality();

const PLATFORM_RADIUS = 12;
// Pillar collision radius — pole 0.45 + drum 0.85 base; use a slightly
// padded value so the player doesn't visually clip the drum.
const PILLAR_COLLISION_RADIUS = 1.0;
const PLATFORM_RIM_HEIGHT = 0.42;
// Path.heightAt returns 0.18 for circleSpinner mode (which double-spinner
// re-uses). Lining the platform top up with that exact value keeps remote
// ghosts (which trust heightAt, no raycast) at the same Y as the local
// player (who snaps via raycast).
const DECK_TOP_Y = 0.18;
const PILLAR_HEIGHT = 4.2;

// Floor tiles — 12 pizza-slice wedges arranged around the central
// pillar (each spanning 30°). One slice can crack + fall at a time,
// leaving a missing pie-piece the player can drop through.
const SLICE_COUNT = 12;
// Chunky pizza slabs — thick wedges, not thin flat plates, so when they
// drop they read as solid blocks of floor crashing into the void. The top
// face stays at DECK_TOP_Y; the extra thickness extends downward.
const TILE_THICKNESS = 1.4;
// Slices butt right up against each other (no angular gap) so there's no
// seam to fall through. Collision is angular (tileAt), but a visible gap
// reads as "fall here" — closing it keeps the disc looking solid.
const TILE_GAP = 0;
// Rounded-edge bevel on each 3D slice.
const TILE_BEVEL = 0.08;
// 12-slice rainbow palette, alternating to read as a real pizza.
const TILE_PALETTE = [
  0xff5fd2, 0xa15cff, 0xffd166, 0xff8a3d,
  0x60a5fa, 0x86efac, 0xff7a99, 0xb9a3ff,
  0xffe28a, 0xffac68, 0x9bd2ff, 0xa3f0c2,
];

const LOWER_BAR_Y = 0.95;
const UPPER_BAR_Y = 2.1;
const LOWER_BAR_COLOR = 0xff8a3d;     // warm orange
const UPPER_BAR_COLOR = 0xffb84a;     // bright yellow-orange
const PILLAR_COLOR    = 0x8b5cf6;     // deep purple
const PILLAR_BAND     = 0xffd166;     // gold band

// Survival spinners — open gentle and ramp slowly. Tile-drops do the
// pressure work; the bars are mostly a positional hazard early on.
const LOWER_INITIAL_VEL = 0.18;        // rad/s
const UPPER_INITIAL_VEL = -0.14;       // rad/s — opposite
const STEP_INTERVAL_SEC = 12;          // bump every 12s
const STEP_FACTOR       = 1.12;        // +12% per step
const MAX_ABS_VEL       = 2.6;

// Falling-tile cadence: a tile begins shaking every TILE_DROP_INTERVAL_SEC.
// Shake briefly, then drop, then stay gone GONE_SEC before respawning.
const TILE_DROP_INTERVAL_SEC = 4.0;    // slower: a new tile starts shaking ~every 4s
const TILE_SHAKE_SEC = 1.4;            // a clear warning before it goes
// How long the falling slab stays in the scene before we cull it. It falls
// under real gravity (TILE_DROP_GRAVITY) the whole time, tumbling as it goes.
const TILE_DROP_SEC = 2.4;
// Downward acceleration of a dropped slab (m/s²). Roughly matches the
// player's own gravity so a player who was standing on it falls alongside.
const TILE_DROP_GRAVITY = 26;
const TILE_GONE_SEC = 22;

export interface DoubleSpinnerHandle {
  group: THREE.Group;
  /** Per-tile meshes the standable-surface raycaster checks against. */
  supportMeshes: THREE.Object3D[];
  /** Survival mode has no gates. */
  gatePositions: number[];
  /** Arena origin in the path's (s, l) frame. */
  arenaCenter: { s: number; l: number };
  /** Outer radius of the platform — used by the engine to detect off-edge. */
  platformRadius: number;
  /** Spin + accel update each frame (race is live). */
  tick: (clockMs: number) => void;
  /** Presentation-only update for the pre-race countdown — animates crowd +
   *  intro title but does NOT drop tiles. */
  tickIdle: (dt: number) => void;
  /** Kick off the "BATTLE ROYALE" 3D intro title animation (pop in, hold,
   *  rise + fade out). Call once when the round is about to begin. */
  triggerIntro: () => void;
  /** The 3D title group — Game.ts yaws this to face the camera each frame
   *  while the intro is visible. */
  introObject: THREE.Object3D;
  /** Is the floor under (s, l) currently passable (i.e. a tile fell)?
   *  Name kept for API parity with the old quadrant version. */
  isQuadrantGone: (s: number, l: number) => boolean;
  /** Central-pillar collision query. Returns a push direction + penetration
   *  if the player is overlapping the pillar's circular footprint. */
  pillarContact: (s: number, l: number, radius: number) => {
    normalS: number;
    normalL: number;
    penetration: number;
  } | null;
  /** Pick a spawn position on a SOLID tile near the rim, indexed by
   *  slotIdx so all peers get different tiles. Returns arena-space
   *  (s, laneOffset). */
  spawnSlot: (slotIdx: number, totalSlots: number) => { s: number; laneOffset: number };
  /** Force the tile at (s, l) (and immediate neighbours) back to solid.
   *  Called on respawn so the player can't drop straight back into a hole. */
  ensureGroundAt: (s: number, l: number) => void;
  /** Player-bar collision (lower OR upper). */
  barContact: (s: number, l: number, radius: number) => {
    normalS: number;
    normalL: number;
    penetration: number;
    barVelS: number;
    barVelL: number;
    bar: "lower" | "upper";
  } | null;
}

type TilePhase = "solid" | "shaking" | "dropping" | "gone";

interface TileEntry {
  mesh: THREE.Mesh;
  /** Slice midpoint in arena-local (l, s). Used by spawn-snap + the
   *  ensure-ground-at helper to know where the wedge "is". */
  cx: number;
  cz: number;
  /** Angular start + end (radians, [0, 2π)) — used by `tileAt(s, l)` to
   *  find which wedge a player is standing on. */
  thetaStart: number;
  thetaEnd: number;
  phase: TilePhase;
  elapsed: number;
  restY: number;
  baseColor: number;
  /** Vertical velocity while in the `dropping` phase (gravity-driven). */
  vy: number;
}

/** A solid 3D pie-wedge with rounded edges. The flat top face ends up at
 *  local y = 0; the slab extends downward to y ≈ -TILE_THICKNESS. */
function buildSliceGeometry(radius: number, thetaStart: number, thetaLen: number): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(Math.cos(thetaStart) * radius, Math.sin(thetaStart) * radius);
  shape.absarc(0, 0, radius, thetaStart, thetaStart + thetaLen, false);
  shape.lineTo(0, 0);
  const geom = new THREE.ExtrudeGeometry(shape, {
    depth: TILE_THICKNESS - TILE_BEVEL * 2,
    bevelEnabled: true,
    bevelThickness: TILE_BEVEL,
    bevelSize: TILE_BEVEL,
    bevelSegments: 2,
    curveSegments: 26,
  });
  // Shape lives in XY extruded along +Z; rotate so it lies flat (extrusion
  // becomes depth in -Y), then drop the top face to y = 0.
  geom.rotateX(Math.PI / 2);
  geom.computeBoundingBox();
  geom.translate(0, -geom.boundingBox!.max.y, 0);
  geom.computeVertexNormals();
  return geom;
}

function buildTiles(radius: number): { group: THREE.Group; tiles: TileEntry[] } {
  const group = new THREE.Group();
  const tiles: TileEntry[] = [];
  const sliceAngle = (Math.PI * 2) / SLICE_COUNT;
  for (let i = 0; i < SLICE_COUNT; i++) {
    const thetaStart = i * sliceAngle + TILE_GAP / 2;
    const thetaLen = sliceAngle - TILE_GAP;
    const baseColor = TILE_PALETTE[i % TILE_PALETTE.length]!;
    const mat = new THREE.MeshStandardMaterial({
      color: baseColor,
      roughness: 0.55,
      metalness: 0.05,
      transparent: true,
      opacity: 1,
    });
    // Each slice is a fully-3D extruded pie wedge with rounded (beveled)
    // edges. Geometry is built so the flat TOP face sits at local y = 0 and
    // the slab extends downward to y = -TILE_THICKNESS, so positioning the
    // mesh at DECK_TOP_Y lines the walkable surface up with heightAt().
    const geom = buildSliceGeometry(radius, thetaStart, thetaLen);
    const mesh = new THREE.Mesh(geom, mat);
    mesh.position.set(0, DECK_TOP_Y, 0);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    // Slice midpoint at 60% of the radius — used as a "where does this
    // slice live" anchor by the spawn-ring / respawn helpers.
    const midTheta = thetaStart + thetaLen / 2;
    const anchorR = radius * 0.6;
    tiles.push({
      mesh,
      cx: Math.cos(midTheta) * anchorR,
      cz: Math.sin(midTheta) * anchorR,
      thetaStart,
      thetaEnd: thetaStart + thetaLen,
      phase: "solid",
      elapsed: 0,
      restY: mesh.position.y,
      baseColor,
      vy: 0,
    });
  }
  return { group, tiles };
}

function buildPlatformDecor(radius: number): THREE.Group {
  const group = new THREE.Group();
  // Low bumper rim — purely VISUAL edge marker. Marked decor-only so
  // the standable-surface raycaster ignores it — otherwise players
  // could climb / get pinned on the torus and never fall off, which
  // softlocks the survival round.
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(radius + 0.05, PLATFORM_RIM_HEIGHT / 2, 14, 96),
    new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.4, metalness: 0.2 }),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = DECK_TOP_Y + PLATFORM_RIM_HEIGHT / 2 - 0.05;
  rim.userData.decorOnly = true;
  group.add(rim);
  // (Removed the purple pedestal base — the disc now reads as floating
  // slices over the void, with nothing solid-looking underneath.)
  return group;
}

function buildPillar(): THREE.Group {
  const group = new THREE.Group();
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.45, 0.5, PILLAR_HEIGHT, 24),
    new THREE.MeshStandardMaterial({ color: PILLAR_COLOR, roughness: 0.5, metalness: 0.12 }),
  );
  // Sit the pillar on the deck TOP (DECK_TOP_Y), independent of how thick
  // the tiles are — the slabs extend downward, so keying off thickness
  // would float the whole pillar.
  pole.position.y = PILLAR_HEIGHT / 2 + DECK_TOP_Y;
  pole.castShadow = true;
  group.add(pole);
  const band = new THREE.Mesh(
    new THREE.CylinderGeometry(0.6, 0.6, 0.35, 24),
    new THREE.MeshStandardMaterial({ color: PILLAR_BAND, roughness: 0.3, metalness: 0.35 }),
  );
  band.position.y = PILLAR_HEIGHT - 0.3 + DECK_TOP_Y;
  group.add(band);
  const drum = new THREE.Mesh(
    new THREE.CylinderGeometry(0.85, 0.9, 0.7, 24),
    new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4 }),
  );
  drum.position.y = DECK_TOP_Y + 0.45;
  group.add(drum);
  for (let i = 0; i < 6; i++) {
    const wedge = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.5, 0.2),
      new THREE.MeshStandardMaterial({ color: 0xef4444 }),
    );
    const a = (i / 6) * Math.PI * 2;
    wedge.position.set(Math.cos(a) * 0.87, drum.position.y, Math.sin(a) * 0.87);
    wedge.rotation.y = -a + Math.PI / 2;
    group.add(wedge);
  }
  return group;
}

function buildSpinnerArm(color: number, halfLength: number, thickness: number): THREE.Group {
  const pivot = new THREE.Group();
  const arm = new THREE.Mesh(
    new THREE.CapsuleGeometry(thickness, halfLength * 2, 12, 24),
    new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.12,
      roughness: 0.4,
      metalness: 0.08,
    }),
  );
  arm.rotation.z = Math.PI / 2;
  arm.castShadow = true;
  pivot.add(arm);
  const tipMat = new THREE.MeshStandardMaterial({ color: 0xa15cff, roughness: 0.35, metalness: 0.2 });
  for (const x of [-halfLength, halfLength]) {
    const tip = new THREE.Mesh(new THREE.SphereGeometry(thickness * 1.25, 18, 14), tipMat);
    tip.position.x = x;
    pivot.add(tip);
  }
  return pivot;
}

function buildPerimeterSign(label: string, color: number): THREE.Group {
  const group = new THREE.Group();
  const post = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.08, 1.2, 12),
    new THREE.MeshStandardMaterial({ color: 0xb45309 }),
  );
  post.position.y = 0.6;
  group.add(post);
  const board = new THREE.Mesh(
    new SoftBox(1.6, 1.0, 0.16),
    new THREE.MeshStandardMaterial({ color, roughness: 0.45 }),
  );
  board.position.y = 1.4;
  group.add(board);
  const tex = makeLetterTexture(label, "#ffffff", 96);
  const label3d = new THREE.Mesh(
    new THREE.PlaneGeometry(1.5, 0.36),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false }),
  );
  label3d.position.set(0, 1.4, 0.09);
  group.add(label3d);
  return group;
}

/**
 * Stadium-style crowd encircling the survival platform. We render the
 * stand as a series of tiered ring-segments using TorusGeometry slices,
 * then sprinkle fans evenly along the arc. The gap is the angular range
 * `[gapStart, gapEnd]` (radians) where no stand is built — that's where
 * a knocked-off player falls into the void.
 */
/** An animatable stadium fan: the placed group + body/leg handles + its
 *  resting Y and a per-fan phase offset so the crowd doesn't jump in unison. */
interface CrowdFan {
  group: THREE.Group;
  body: THREE.Group;
  leftLeg: THREE.Group;
  rightLeg: THREE.Group;
  baseY: number;
  phase: number;
}

function buildCrowdRing(platformRadius: number): { group: THREE.Group; fans: CrowdFan[] } {
  const group = new THREE.Group();
  const fans: CrowdFan[] = [];
  // Inner edge of the seating starts well past the rim — far enough that a
  // player can't jump the gap onto it, so a knock-off is a real fall.
  const innerR = platformRadius + 11;
  const tierDepth = 1.4;
  const seatMat = new THREE.MeshStandardMaterial({ color: 0x4a4a5a, roughness: 0.65 });
  const groundMat = new THREE.MeshStandardMaterial({ color: 0x3aa05c, roughness: 0.7 });

  // Forward gap — kept wide enough that a player shoved off the front
  // edge falls straight past the crowd. ~90° opening centred on +S.
  const gapHalf = Math.PI * 0.28;             // ±~50° from forward (+S axis)
  // Build the seating as a continuous arc on the opposite 270°.
  const arcStart = gapHalf;                   // start just past the gap
  const arcLen = Math.PI * 2 - gapHalf * 2;   // remaining 270°

  // 3 tiers stepping up + back, each with its own torus segment.
  const TIERS = 3;
  for (let tier = 0; tier < TIERS; tier++) {
    const tierInner = innerR + tier * tierDepth;
    const tierOuter = tierInner + tierDepth;
    const tierY = 0.6 + tier * 1.0;
    // Torus is tubular; we want a flat ring-segment, so use a Ring/Lathe
    // approach via a Shape extruded around the arc. Simpler: cylinder
    // with thetaStart / thetaLength, scaled to a thin ring by giving the
    // inner radius via a hole in a Shape.
    const ringShape = new THREE.Shape();
    const seg = 48;
    // moveTo the first point — otherwise the shape starts at (0,0) and
    // ExtrudeGeometry draws a long spoke from arena centre to the ring,
    // which shows up as a stray line across the playing area.
    {
      const a0 = arcStart;
      ringShape.moveTo(Math.cos(a0) * tierOuter, Math.sin(a0) * tierOuter);
    }
    for (let i = 1; i <= seg; i++) {
      const a = arcStart + (i / seg) * arcLen;
      ringShape.lineTo(Math.cos(a) * tierOuter, Math.sin(a) * tierOuter);
    }
    for (let i = seg; i >= 0; i--) {
      const a = arcStart + (i / seg) * arcLen;
      ringShape.lineTo(Math.cos(a) * tierInner, Math.sin(a) * tierInner);
    }
    ringShape.closePath();
    const geom = new THREE.ExtrudeGeometry(ringShape, {
      depth: 1.2,
      bevelEnabled: false,
      curveSegments: 24,
    });
    const tierMesh = new THREE.Mesh(geom, seatMat);
    // ExtrudeGeometry extrudes along +Z; rotate so it stands as a ring
    // around Y.
    tierMesh.rotation.x = -Math.PI / 2;
    tierMesh.position.y = tierY;
    tierMesh.receiveShadow = true;
    tierMesh.castShadow = true;
    group.add(tierMesh);

    // Sprinkle fans along this tier. Each is a full character model — on the
    // low tier we keep the cheap seating rings but skip the ~78 characters.
    const fanCount = ARENA_LOW ? 0 : Math.floor(arcLen / 0.18); // ~one fan every 10°
    const fanR = (tierInner + tierOuter) / 2;
    for (let i = 0; i < fanCount; i++) {
      const a = arcStart + ((i + 0.5) / fanCount) * arcLen;
      const built = makeCheeringFan(tier * 31 + i);
      const baseY = tierY + 1.2 + 0.22;
      built.group.position.set(Math.cos(a) * fanR, baseY, Math.sin(a) * fanR);
      built.group.rotation.y = -a + Math.PI / 2 + Math.PI; // face inward toward arena
      group.add(built.group);
      fans.push({
        group: built.group,
        body: built.body,
        leftLeg: built.leftLeg,
        rightLeg: built.rightLeg,
        baseY,
        // Spread phases by seat so the jumping ripples around the stands.
        phase: tier * 1.7 + i * 0.6,
      });
    }
  }

  // Green ground pad under the entire stand arc so the stands don't
  // float over the void. Same shape, no inner cut, fatter outer radius.
  const padShape = new THREE.Shape();
  const padInner = innerR - 0.4;
  const padOuter = innerR + TIERS * tierDepth + 1.5;
  const seg2 = 64;
  {
    const a0 = arcStart;
    padShape.moveTo(Math.cos(a0) * padOuter, Math.sin(a0) * padOuter);
  }
  for (let i = 1; i <= seg2; i++) {
    const a = arcStart + (i / seg2) * arcLen;
    padShape.lineTo(Math.cos(a) * padOuter, Math.sin(a) * padOuter);
  }
  for (let i = seg2; i >= 0; i--) {
    const a = arcStart + (i / seg2) * arcLen;
    padShape.lineTo(Math.cos(a) * padInner, Math.sin(a) * padInner);
  }
  padShape.closePath();
  const pad = new THREE.Mesh(
    new THREE.ExtrudeGeometry(padShape, { depth: 0.4, bevelEnabled: false, curveSegments: 32 }),
    groundMat,
  );
  pad.rotation.x = -Math.PI / 2;
  pad.position.y = 0.0;
  pad.receiveShadow = true;
  group.add(pad);

  return { group, fans };
}

const letterTexCache = new Map<string, THREE.CanvasTexture>();
function makeLetterTexture(text: string, color = "#ffffff", fontSize = 220): THREE.CanvasTexture {
  const key = `${text}|${color}|${fontSize}`;
  const cached = letterTexCache.get(key);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, 256, 256);
  ctx.fillStyle = color;
  ctx.font = `900 ${fontSize}px Nunito, Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 128, 138);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  letterTexCache.set(key, tex);
  return tex;
}

export function buildDoubleSpinnerArena(path: Path): DoubleSpinnerHandle {
  const arenaS = path.length / 2;
  const arenaL = 0;

  const group = new THREE.Group();
  group.position.set(arenaL, 0, arenaS);

  const { group: tilesGroup, tiles } = buildTiles(PLATFORM_RADIUS);
  group.add(tilesGroup);
  group.add(buildPlatformDecor(PLATFORM_RADIUS));

  group.add(buildPillar());

  // ── "BATTLE ROYALE" 3D intro title ───────────────────────────────────
  // Real extruded 3D geometry (not a flat overlay) floating above the
  // arena: pops in, holds, then rises + fades. Driven by triggerIntro() at
  // round start; Game.ts yaws `introGroup` each frame to face the camera.
  const introGroup = new THREE.Group();
  introGroup.position.set(0, 6.6, 0);
  introGroup.visible = false;
  const introMat = new THREE.MeshStandardMaterial({
    color: 0xffd54a,
    metalness: 0.35,
    roughness: 0.28,
    emissive: 0xff7a1e,
    emissiveIntensity: 0.3,
    transparent: true,
    opacity: 0,
  });
  {
    const font = new Font(helvetikerBold as unknown as ConstructorParameters<typeof Font>[0]);
    const makeLine = (text: string, y: number) => {
      const g = new TextGeometry(text, {
        font,
        size: 1.5,
        depth: 0.4,
        curveSegments: 5,
        bevelEnabled: true,
        bevelThickness: 0.07,
        bevelSize: 0.05,
        bevelSegments: 2,
      });
      g.center();
      const m = new THREE.Mesh(g, introMat);
      m.position.y = y;
      m.castShadow = true;
      introGroup.add(m);
    };
    makeLine("BATTLE", 1.0);
    makeLine("ROYALE", -1.0);
  }
  group.add(introGroup);
  let introActive = false;
  let introT = 0;

  // ESL perimeter signs (decorative).
  const signs = [
    { label: "NOUNS",      color: 0x86efac, angle: Math.PI * 0.85 },
    { label: "VERBS",      color: 0x86efac, angle: Math.PI * 0.15 },
    { label: "ADJECTIVES", color: 0xfde68a, angle: Math.PI * 1.85 },
  ];
  for (const s of signs) {
    const sign = buildPerimeterSign(s.label, s.color);
    // Out by the stands (which now sit far from the rim) so they're not
    // stranded floating in the void between platform and crowd.
    const r = PLATFORM_RADIUS + 12.5;
    sign.position.set(Math.cos(s.angle) * r, 0.2, Math.sin(s.angle) * r);
    sign.rotation.y = -s.angle + Math.PI / 2;
    group.add(sign);
  }

  // Crowd ring — tiered stands packed with cheering fans encircling the
  // platform, leaving a forward-facing gap so a player knocked off the
  // edge falls into the void instead of into the stands. Stands sit
  // OUTSIDE the platform radius (their inner edge ~3m past the rim) so
  // they don't extend any usable surface.
  const crowd = buildCrowdRing(PLATFORM_RADIUS);
  group.add(crowd.group);
  const crowdFans = crowd.fans;

  // Spinner bars.
  const lowerHalfLen = PLATFORM_RADIUS - 1.0;
  const lowerThickness = 0.45;
  const upperHalfLen = PLATFORM_RADIUS - 1.4;
  const upperThickness = 0.42;
  const lowerPivot = buildSpinnerArm(LOWER_BAR_COLOR, lowerHalfLen, lowerThickness);
  lowerPivot.position.y = DECK_TOP_Y + LOWER_BAR_Y;
  group.add(lowerPivot);
  const upperPivot = buildSpinnerArm(UPPER_BAR_COLOR, upperHalfLen, upperThickness);
  upperPivot.position.y = DECK_TOP_Y + UPPER_BAR_Y;
  upperPivot.rotation.y = Math.PI / 2;
  group.add(upperPivot);

  // ── Persisted state ───────────────────────────────────────────────
  let lowerAngle = 0;
  let upperAngle = Math.PI / 2;
  let lowerVel = LOWER_INITIAL_VEL;
  let upperVel = UPPER_INITIAL_VEL;
  let lastClockMs = 0;
  let elapsedSec = 0;
  // Presentation clock for crowd + intro — advances every frame (including
  // the pre-race countdown via tickIdle) so the stands cheer the whole time,
  // independent of the tile-drop schedule which only runs once the race is live.
  let presoClock = 0;
  let nextStepAtSec = STEP_INTERVAL_SEC;
  let nextTileDropAtSec = TILE_DROP_INTERVAL_SEC;
  let lastPickedIdx = -1;

  // Deterministic pick — hash the integer-second timestamp so all clients
  // start the same tile shaking at the same time.
  const pickTile = (atSec: number): number => {
    const seed = Math.floor(atSec * 1000);
    let h = seed ^ 0x9e3779b9;
    h = (h ^ (h >>> 16)) * 0x85ebca6b;
    h = (h ^ (h >>> 13)) * 0xc2b2ae35;
    h ^= h >>> 16;
    let attempts = 0;
    let idx = Math.abs(h) % tiles.length;
    // Walk forward until we find a solid tile not equal to last pick.
    while (attempts < tiles.length) {
      const t = tiles[idx]!;
      if (t.phase === "solid" && idx !== lastPickedIdx) return idx;
      idx = (idx + 1) % tiles.length;
      attempts++;
    }
    return idx;
  };

  // Crowd + intro title animation — runs every frame, even during the
  // pre-race countdown (via tickIdle), so the stands are always alive.
  const animatePresentation = (dt: number) => {
    presoClock += dt;

    // ── Intro 3D title animation ─────────────────────────────────────────
    if (introActive) {
      introT += dt;
      if (introT >= 3.8) {
        introActive = false;
        introGroup.visible = false;
        introMat.opacity = 0;
      } else {
        // 0–0.45s: pop in (overshoot ease). Hold until 2.8s. 2.8–3.8s: rise + fade.
        introGroup.visible = true;
        const popIn = Math.min(1, introT / 0.45);
        const ease = 1 - Math.pow(1 - popIn, 3);
        let opacity = 1, yRise = 0, scaleBoost = 1;
        if (introT > 2.8) {
          const out = (introT - 2.8) / 1.0;
          opacity = 1 - out;
          yRise = out * 3.5;
          scaleBoost = 1 + out * 0.2;
        }
        introMat.opacity = Math.max(0, opacity) * ease;
        const s = (0.6 + 0.4 * ease) * scaleBoost;
        introGroup.scale.setScalar(s);
        introGroup.position.y = 6.6 + yRise + Math.sin(introT * 3) * 0.08;
      }
    }

    // ── Excited crowd: cheering + jumping ────────────────────────────────
    for (let i = 0; i < crowdFans.length; i++) {
      const f = crowdFans[i]!;
      const tt = presoClock * 6.5 + f.phase;          // brisk bounce
      const bounce = Math.abs(Math.sin(tt));
      // Every ~1.3s each fan throws in a big celebratory leap.
      const leapPhase = (presoClock * 0.78 + f.phase * 0.37) % 1;
      const leap = leapPhase < 0.24 ? Math.sin((leapPhase / 0.24) * Math.PI) : 0;
      const jump = bounce * 0.45 + leap * 0.95;
      f.group.position.y = f.baseY + jump;
      // Lean + a little twist so they read as animated, not pogo-sticks.
      f.body.rotation.z = Math.sin(tt * 0.5) * 0.18;
      f.body.rotation.x = -leap * 0.25;
      f.body.rotation.y = Math.sin(tt * 0.33 + f.phase) * 0.35;
      // Tuck the legs up while airborne (knees-up cheer jump).
      const tuck = -(bounce * 0.35 + leap * 0.9);
      f.leftLeg.rotation.x = tuck;
      f.rightLeg.rotation.x = tuck;
    }
  };

  const tick = (clockMs: number) => {
    const dtMs = lastClockMs === 0 ? 16 : Math.max(0, Math.min(64, clockMs - lastClockMs));
    lastClockMs = clockMs;
    const dt = dtMs / 1000;
    animatePresentation(dt);
    elapsedSec += dt;

    // Spinner ramp.
    while (elapsedSec >= nextStepAtSec) {
      lowerVel = Math.max(-MAX_ABS_VEL, Math.min(MAX_ABS_VEL, lowerVel * STEP_FACTOR));
      upperVel = Math.max(-MAX_ABS_VEL, Math.min(MAX_ABS_VEL, upperVel * STEP_FACTOR));
      nextStepAtSec += STEP_INTERVAL_SEC;
    }
    lowerAngle += lowerVel * dt;
    upperAngle += upperVel * dt;
    lowerPivot.rotation.y = lowerAngle;
    upperPivot.rotation.y = upperAngle;

    // ── Tile drop scheduler ──────────────────────────────────────────
    while (elapsedSec >= nextTileDropAtSec) {
      const idx = pickTile(nextTileDropAtSec);
      const t = tiles[idx]!;
      if (t.phase === "solid") {
        t.phase = "shaking";
        t.elapsed = 0;
        lastPickedIdx = idx;
      }
      nextTileDropAtSec += TILE_DROP_INTERVAL_SEC;
    }

    // Tile state machines.
    for (let k = 0; k < tiles.length; k++) {
      const t = tiles[k]!;
      t.elapsed += dt;
      const mesh = t.mesh;
      const mat = mesh.material as THREE.MeshStandardMaterial;
      switch (t.phase) {
        case "solid": {
          mesh.position.x = 0;
          mesh.position.z = 0;
          mesh.position.y = t.restY;
          mesh.rotation.set(0, 0, 0);
          mesh.scale.set(1, 1, 1); // clear any leftover shake pulse scale
          if (mat.opacity !== 1) mat.opacity = 1;
          mat.emissiveIntensity = 0;
          mat.color.setHex(t.baseColor);
          break;
        }
        case "shaking": {
          const tt = t.elapsed * 24;
          // Faster, larger XZ jitter — the doomed slice clearly trembles.
          mesh.position.x = Math.sin(tt) * 0.12;
          mesh.position.z = Math.cos(tt * 1.07) * 0.12;
          mesh.position.y = t.restY + Math.sin(tt * 1.3) * 0.05;
          const flash = Math.min(1, t.elapsed / TILE_SHAKE_SEC);
          // Strong red flash that ramps as the shake builds.
          const pulse = (Math.sin(tt * 1.8) * 0.5 + 0.5);
          mat.color.lerpColors(new THREE.Color(t.baseColor), new THREE.Color(0xff2a2a), 0.45 + 0.45 * flash);
          mat.emissive = new THREE.Color(0xff2020);
          mat.emissiveIntensity = (0.4 + pulse * 0.9) * (0.5 + flash);
          // Pulse the slice ~5% larger so even peripheral-vision picks it up.
          const scale = 1 + pulse * 0.05 * flash;
          mesh.scale.set(scale, 1, scale);
          if (t.elapsed >= TILE_SHAKE_SEC) {
            t.phase = "dropping";
            t.elapsed = 0;
            t.vy = 0;
            // Clear the shake jitter so the slab drops straight down from
            // its rest pose.
            mesh.position.x = 0;
            mesh.position.z = 0;
            mesh.position.y = t.restY;
            mesh.scale.set(1, 1, 1); // clear shake pulse scale
            mat.color.setHex(t.baseColor);
            mat.emissiveIntensity = 0;
          }
          break;
        }
        case "dropping": {
          // Real gravity, straight down — no tumble. NO opacity fade: it
          // stays a solid slab so a player standing on it visibly falls
          // along with it, dropping cleanly into the void.
          t.vy -= TILE_DROP_GRAVITY * dt;
          mesh.position.y += t.vy * dt;
          if (t.elapsed >= TILE_DROP_SEC) {
            t.phase = "gone";
            t.elapsed = 0;
            mesh.visible = false;
          }
          break;
        }
        case "gone": {
          if (t.elapsed >= TILE_GONE_SEC) {
            t.phase = "solid";
            t.elapsed = 0;
            t.vy = 0;
            mesh.visible = true;
            mesh.position.set(0, t.restY, 0);
            mesh.rotation.set(0, 0, 0);
            mat.opacity = 1;
            mat.emissiveIntensity = 0;
            mat.color.setHex(t.baseColor);
          }
          break;
        }
      }
    }
  };

  const triggerIntro = () => {
    introActive = true;
    introT = 0;
    introGroup.visible = true;
    introMat.opacity = 0;
    introGroup.scale.setScalar(0.001);
  };

  // Presentation-only step for the pre-race countdown: animates the crowd +
  // intro title WITHOUT advancing the tile-drop schedule (no tile is lost
  // before GO!).
  const tickIdle = (dt: number) => {
    animatePresentation(dt);
  };

  /** Find the pizza slice under (s, l). Uses floor-index math so the
   *  tiny visual gap between slices never causes a "no slice" fall-
   *  through — every angle maps to exactly one slice. */
  const sliceAngle = (Math.PI * 2) / SLICE_COUNT;
  const tileAt = (s: number, l: number): TileEntry | null => {
    const dl = l - arenaL;
    const ds = s - arenaS;
    let theta = Math.atan2(ds, dl);
    if (theta < 0) theta += Math.PI * 2;
    const idx = Math.floor(theta / sliceAngle) % SLICE_COUNT;
    return tiles[idx] ?? null;
  };

  const pillarContact = (s: number, l: number, radius: number) => {
    const dl = l - arenaL;
    const ds = s - arenaS;
    const sumR = PILLAR_COLLISION_RADIUS + radius;
    const dist2 = dl * dl + ds * ds;
    if (dist2 >= sumR * sumR) return null;
    const dist = Math.sqrt(Math.max(0.0001, dist2));
    return {
      normalS: dist > 0.001 ? ds / dist : 0,
      normalL: dist > 0.001 ? dl / dist : 1,
      penetration: sumR - dist,
    };
  };

  const spawnSlot = (slotIdx: number, totalSlots: number) => {
    // Ring of spawn points just outside the pillar so multiple players
    // can stand on different tiles without immediately overlapping.
    const n = Math.max(1, totalSlots);
    const angle = (slotIdx / n) * Math.PI * 2;
    const r = PILLAR_COLLISION_RADIUS + 3.5; // ~4.5m from centre
    return {
      s: arenaS + Math.sin(angle) * r,
      laneOffset: Math.cos(angle) * r,
    };
  };

  const ensureGroundAt = (s: number, l: number) => {
    // Reset the slice under the spawn point + its two immediate
    // neighbours so the player doesn't drop straight back into a hole.
    const here = tileAt(s, l);
    if (!here) return;
    const idx = tiles.indexOf(here);
    const candidates = [tiles[(idx - 1 + SLICE_COUNT) % SLICE_COUNT]!, here, tiles[(idx + 1) % SLICE_COUNT]!];
    for (const t of candidates) {
      t.phase = "solid";
      t.elapsed = 0;
      t.vy = 0;
      t.mesh.visible = true;
      const mat = t.mesh.material as THREE.MeshStandardMaterial;
      mat.opacity = 1;
      mat.emissiveIntensity = 0;
      mat.color.setHex(t.baseColor);
      t.mesh.position.set(0, t.restY, 0);
      t.mesh.rotation.set(0, 0, 0);
    }
  };

  const isQuadrantGone = (s: number, l: number) => {
    const t = tileAt(s, l);
    if (!t) return false;
    return t.phase === "dropping" || t.phase === "gone";
  };

  // Only solid tiles act as supports. The raycaster filters by mesh.visible
  // so hiding gone tiles already excludes them; we still expose the full
  // list because shaking tiles remain supports until they drop.
  const supportMeshes: THREE.Object3D[] = tiles.map((t) => t.mesh);

  const barContact = (s: number, l: number, radius: number) => {
    const dl = l - arenaL;
    const ds = s - arenaS;
    const rOut = Math.max(lowerHalfLen, upperHalfLen) + radius + 0.2;
    if (dl * dl + ds * ds > rOut * rOut) return null;

    const lower = barRectContact(
      ds, dl, lowerAngle, lowerHalfLen, lowerThickness, radius,
      lowerVel,
    );
    if (lower) return { ...lower, bar: "lower" as const };
    const upper = barRectContact(
      ds, dl, upperAngle, upperHalfLen, upperThickness, radius,
      upperVel,
    );
    if (upper) return { ...upper, bar: "upper" as const };
    return null;
  };

  return {
    group,
    supportMeshes,
    gatePositions: [],
    arenaCenter: { s: arenaS, l: arenaL },
    platformRadius: PLATFORM_RADIUS,
    tick,
    tickIdle,
    triggerIntro,
    introObject: introGroup,
    barContact,
    pillarContact,
    spawnSlot,
    isQuadrantGone,
    ensureGroundAt,
  };
}

function barRectContact(
  ds: number, dl: number,
  angle: number,
  halfLen: number, halfThick: number,
  radius: number,
  angularVel: number,
) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const localX = dl * cos - ds * sin;
  const localY = dl * sin + ds * cos;
  const clampedX = Math.max(-halfLen, Math.min(halfLen, localX));
  const dxLocal = localX - clampedX;
  const dyLocal = localY;
  const dist2 = dxLocal * dxLocal + dyLocal * dyLocal;
  const sumR = halfThick + radius;
  if (dist2 >= sumR * sumR) return null;
  const dist = Math.sqrt(Math.max(0.0001, dist2));
  const nxLocal = dist > 0.001 ? dxLocal / dist : 0;
  const nyLocal = dist > 0.001 ? dyLocal / dist : 1;
  const normalL = nxLocal * cos + nyLocal * sin;
  const normalS = -nxLocal * sin + nyLocal * cos;
  const barVelL = angularVel * ds;
  const barVelS = -angularVel * dl;
  return {
    normalS,
    normalL,
    penetration: sumR - dist,
    barVelS,
    barVelL,
  };
}
